import {
  cacheKeys,
  computeCommonGroups,
  computeGroupChanges,
  Group,
  UPDATE_ASSETS_GROUPS,
  useAllWorkspaceGroups,
  useCreateGroup,
  useCurrentWorkspace,
  useCustomMutation,
} from '@gripp/shared-logic';
import {
  ColumnData,
  SelectionStatus,
  SlideLayout,
  SlidePanelRegistrar,
} from '@gripp/shared-ui';
import { useQueryClient } from '@tanstack/react-query';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { AssetComponent, SelectedAssetRow } from './assetComponent';
import { AssetsFilters } from './assetsFilters';
import { AssetsGroupsPicker } from './assetsGroupsPicker';
import {
  AssetsSelectionPanelHeaderActions
} from './assetsSelectionPanel';

export type AssetsShellOutletContext = SlidePanelRegistrar & {
  hasSelection: boolean;
  clearSelection: () => void;
};

export const AssetsShell = () => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { createGroup } = useCreateGroup();
  const workspace = useCurrentWorkspace(true);
  const currentWorkspaceId = workspace?.id;

  const { groups: availableGroups } = useAllWorkspaceGroups(currentWorkspaceId);

  const [rowsSelected, setRowsSelected] = useState<SelectedAssetRow[]>([]);
  const [selectedGroups, setSelectedGroups] = useState<Group[]>([]);
  const [selectionStatus, setSelectionStatus] =
    useState<SelectionStatus>('idle');

  const rowsSelectedIds = useMemo(
    () => rowsSelected.map((r) => r.id),
    [rowsSelected]
  );

  const hasSelection = rowsSelected.length > 0;
  
  const { mutateAsync: updateAssetsGroups } = useCustomMutation({
    query: UPDATE_ASSETS_GROUPS,
  });

  useEffect(() => {
    if (!hasSelection) {
      setSelectedGroups([]);
      return;
    }
    
    const nextSelected = computeCommonGroups(
      rowsSelected,
      (row) => row.groups ?? [],
      availableGroups
    ).map((g) => ({ id: g.id, name: g.name }));

    setSelectedGroups(nextSelected);
  }, [rowsSelected, availableGroups, hasSelection]);

  const clearSelection = useCallback(() => {
    setRowsSelected([]);
    setSelectionStatus('reset');
  }, []);

  // SlideLayout's `rightColumn` prop now wins over outlet-registered panels,
  // so an active selection panel would otherwise stay visible after the user
  // navigates to /assets/:id, /assets/filters, etc. Drop the selection on
  // any sub-route change so the navigated panel becomes visible. Skip the
  // initial render so first mounting at any path doesn't fire a no-op clear
  // (which would still re-broadcast `selectionStatus: 'reset'` to the grid).
  const prevPathnameRef = useRef(location.pathname);
  useLayoutEffect(() => {
    if (prevPathnameRef.current === location.pathname) return;
    prevPathnameRef.current = location.pathname;
    clearSelection();
  }, [location.pathname, clearSelection]);

  const handleGroupsChange = useCallback(
    async (nextGroups: Group[]) => {
      const { added, removed } = computeGroupChanges(selectedGroups, nextGroups);

      setSelectedGroups(nextGroups);

      if (!currentWorkspaceId) {
        return;
      }

      await updateAssetsGroups({
        input: {
          addedGroups: added.map((g) => ({ id: g.id })),
          removedGroups: removed.map((g) => ({ id: g.id })),
          assetIds: rowsSelectedIds,
          workspaceId: currentWorkspaceId,
        },
      });

      await queryClient.invalidateQueries({
        queryKey: cacheKeys('asset').all,
      });
    }, [
      selectedGroups,
      rowsSelectedIds,
      currentWorkspaceId,
      updateAssetsGroups,
      queryClient
    ]
  );

  const outletContext = useMemo(
    () => ({ hasSelection, clearSelection }),
    [hasSelection, clearSelection]
  );

  const leftColumn: ColumnData = {
    title: 'assets.menu.title',
    mainComponent: (
      <AssetComponent
        onRowsSelectedChange={setRowsSelected}
        selectedRowIds={rowsSelectedIds}
        selectionStatus={selectionStatus}
        onSelectionStatusChange={setSelectionStatus}
      />
    ),
    headerComponent: <AssetsFilters />,
    disableScroll: true,
  };

  // Fallbacks: SlideLayout uses these when no child route claims the slot.
  // A navigated panel (e.g. /assets/:id, /assets/filters) takes priority so a
  // URL change reflects in the UI instead of staying stuck on the selection
  // view.
  const selectionRightColumn = useMemo<ColumnData | undefined>(() => {
    if (!hasSelection || !currentWorkspaceId) {
      return undefined;
    }

    const title =
      rowsSelected.length === 1
        ? t('assets.topBar.assetSelected')
        : t('assets.topBar.assetsSelected', {
            count: rowsSelected.length,
          });
    i18n.addResource(i18n.language, 'translation', title, title);

    return {
      title,
      mainComponent: (
        <AssetsGroupsPicker
          selectedGroups={selectedGroups}
          onGroupsChange={handleGroupsChange}
          onCreateGroup={createGroup}
          workspaceId={currentWorkspaceId}
        />
      ),
      headerComponent: (
        <AssetsSelectionPanelHeaderActions onReset={clearSelection} />
      ),
      showBackButton: true,
      onBackButtonPress: clearSelection,
      disableScroll: false,
    };
  }, [
    hasSelection,
    currentWorkspaceId,
    rowsSelected,
    selectedGroups,
    handleGroupsChange,
    clearSelection,
    createGroup,
    t,
    i18n,
  ]);

  return (
    <SlideLayout
      leftColumn={leftColumn}
      rightColumn={selectionRightColumn}
      enableChildRoutes
      outletContext={outletContext}
    />
  );
};
